/**
 * Count Up — vanilla port of React Bits CountUp
 * https://github.com/DavidHDev/react-bits (TextAnimations/CountUp)
 * https://www.reactbits.dev/text-animations/count-up
 *
 * Original uses motion/react spring; this ports the same damping/stiffness
 * mapping with a lightweight spring integrator (no Motion dependency).
 *
 * Usage:
 *   <span data-count-up data-to="100" data-from="0" data-duration="2" data-separator=","></span>
 *   CountUp.autoInit();
 *
 *   Or: CountUp.init(el, { to, from, direction, delay, duration, separator, startWhen, onStart, onEnd })
 */
(function (global) {
  "use strict";

  function num(v, fallback) {
    if (v == null || v === "") return fallback;
    var n = Number(v);
    return isFinite(n) ? n : fallback;
  }

  function parseBool(v, fallback) {
    if (v == null || v === "") return fallback;
    if (v === true || v === false) return v;
    var s = String(v).toLowerCase();
    if (s === "false" || s === "0") return false;
    if (s === "true" || s === "1") return true;
    return fallback;
  }

  function getDecimalPlaces(n) {
    var str = String(n);
    if (str.indexOf(".") !== -1) {
      var decimals = str.split(".")[1];
      if (parseInt(decimals, 10) !== 0) return decimals.length;
    }
    return 0;
  }

  function readOpts(el, options) {
    options = options || {};
    function d(name, attr) {
      return options[name] != null ? options[name] : el && el.getAttribute(attr);
    }
    return {
      to: num(d("to", "data-to"), 100),
      from: num(d("from", "data-from"), 0),
      direction: d("direction", "data-direction") || "up",
      delay: num(d("delay", "data-delay"), 0),
      duration: num(d("duration", "data-duration"), 2),
      separator: d("separator", "data-separator") != null ? String(d("separator", "data-separator")) : "",
      startWhen: parseBool(d("startWhen", "data-start-when"), true),
      once: parseBool(d("once", "data-once"), true),
      onStart: typeof options.onStart === "function" ? options.onStart : null,
      onEnd: typeof options.onEnd === "function" ? options.onEnd : null
    };
  }

  function formatFactory(from, to, separator) {
    var maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));
    return function formatValue(latest) {
      var hasDecimals = maxDecimals > 0;
      var options = {
        useGrouping: !!separator,
        minimumFractionDigits: hasDecimals ? maxDecimals : 0,
        maximumFractionDigits: hasDecimals ? maxDecimals : 0
      };
      var formatted = new Intl.NumberFormat("en-US", options).format(latest);
      return separator ? formatted.replace(/,/g, separator) : formatted;
    };
  }

  /**
   * Approximate Framer Motion spring:
   * damping = 20 + 40 / duration
   * stiffness = 100 / duration
   */
  function Spring(initial, damping, stiffness) {
    this.value = initial;
    this.velocity = 0;
    this.target = initial;
    this.damping = damping;
    this.stiffness = stiffness;
    this.listeners = [];
    this.raf = 0;
    this.lastTs = 0;
    this._tick = this._tick.bind(this);
  }

  Spring.prototype.on = function (fn) {
    this.listeners.push(fn);
    return function unsubscribe() {
      var i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    }.bind(this);
  };

  Spring.prototype._emit = function () {
    for (var i = 0; i < this.listeners.length; i++) {
      this.listeners[i](this.value);
    }
  };

  Spring.prototype.set = function (target) {
    this.target = target;
    if (!this.raf) {
      this.lastTs = 0;
      this.raf = requestAnimationFrame(this._tick);
    }
  };

  Spring.prototype._tick = function (ts) {
    if (!this.lastTs) this.lastTs = ts;
    var dt = Math.min(0.032, (ts - this.lastTs) / 1000);
    this.lastTs = ts;

    // mass = 1; a = -k(x - target) - c v
    var x = this.value;
    var v = this.velocity;
    var a = -this.stiffness * (x - this.target) - this.damping * v;
    v += a * dt;
    x += v * dt;
    this.velocity = v;
    this.value = x;

    this._emit();

    var settled =
      Math.abs(this.target - this.value) < 0.001 && Math.abs(this.velocity) < 0.01;
    if (settled) {
      this.value = this.target;
      this.velocity = 0;
      this._emit();
      this.raf = 0;
      this.lastTs = 0;
      return;
    }
    this.raf = requestAnimationFrame(this._tick);
  };

  Spring.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.listeners = [];
  };

  function CountUpInstance(el, options) {
    this.el = el;
    this.opts = readOpts(el, options);
    this.format = formatFactory(this.opts.from, this.opts.to, this.opts.separator);
    this.started = false;
    this.inView = false;
    this.timers = [];
    this.io = null;

    var duration = Math.max(this.opts.duration, 0.05);
    var damping = 20 + 40 * (1 / duration);
    var stiffness = 100 * (1 / duration);
    var initial = this.opts.direction === "down" ? this.opts.to : this.opts.from;

    this.spring = new Spring(initial, damping, stiffness);
    this.el.textContent = this.format(initial);

    this._onSpring = function (latest) {
      this.el.textContent = this.format(latest);
    }.bind(this);
    this._unsub = this.spring.on(this._onSpring);

    // Prefer a stable observe target (section media/overlays) over tiny absolute spans
    this.observeEl = resolveObserveEl(el);

    this._onIntersect = function (entries) {
      var entry = entries[0];
      if (!entry) return;

      var nowIn = !!entry.isIntersecting;
      if (nowIn === this.inView) return; // ignore duplicate callbacks while staying in/out

      if (nowIn) {
        this.inView = true;
        if (this.opts.once) {
          this.start();
          if (this.io) {
            this.io.disconnect();
            this.io = null;
          }
        } else {
          this.restart();
        }
        return;
      }

      this.inView = false;
      if (!this.opts.once) {
        this.reset();
      }
    }.bind(this);

    if ("IntersectionObserver" in global) {
      this.io = new IntersectionObserver(this._onIntersect, {
        threshold: 0.15,
        rootMargin: "0px 0px -8% 0px"
      });
      this.io.observe(this.observeEl);
    } else if (this.opts.startWhen) {
      this.start();
    }
  }

  function resolveObserveEl(el) {
    var sel = el.getAttribute("data-observe");
    if (sel) {
      try {
        var found = el.closest(sel) || document.querySelector(sel);
        if (found) return found;
      } catch (e) {
        /* invalid selector */
      }
    }
    return (
      el.closest("[data-count-up-observe]") ||
      el.closest(".go-serise-smart__media") ||
      el.closest(".go-serise-smart__overlays") ||
      el
    );
  }

  CountUpInstance.prototype.start = function () {
    if (this.started || !this.opts.startWhen) return;
    this.started = true;

    if (typeof this.opts.onStart === "function") this.opts.onStart();

    var self = this;
    var delayMs = this.opts.delay * 1000;
    var durationMs = this.opts.duration * 1000;
    var target = this.opts.direction === "down" ? this.opts.from : this.opts.to;

    var t1 = setTimeout(function () {
      self.spring.set(target);
    }, delayMs);
    var t2 = setTimeout(function () {
      if (typeof self.opts.onEnd === "function") self.opts.onEnd();
    }, delayMs + durationMs);

    this.timers.push(t1, t2);
  };

  CountUpInstance.prototype.reset = function () {
    this.started = false;
    this.timers.forEach(clearTimeout);
    this.timers = [];
    var initial = this.opts.direction === "down" ? this.opts.to : this.opts.from;
    if (this.spring) {
      if (this.spring.raf) {
        cancelAnimationFrame(this.spring.raf);
        this.spring.raf = 0;
        this.spring.lastTs = 0;
      }
      this.spring.value = initial;
      this.spring.velocity = 0;
      this.spring.target = initial;
    }
    this.el.textContent = this.format(initial);
  };

  CountUpInstance.prototype.restart = function () {
    this.reset();
    this.start();
  };

  CountUpInstance.prototype.destroy = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this._unsub) this._unsub();
    if (this.spring) this.spring.destroy();
  };

  var CountUp = {
    init: function (el, options) {
      if (!el) return null;
      return new CountUpInstance(el, options);
    },
    autoInit: function (root) {
      root = root || document;
      var nodes = root.querySelectorAll("[data-count-up]");
      var list = [];
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].__countUp) continue;
        var inst = CountUp.init(nodes[i]);
        nodes[i].__countUp = inst;
        list.push(inst);
      }
      return list;
    }
  };

  global.CountUp = CountUp;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      CountUp.autoInit();
    });
  } else {
    CountUp.autoInit();
  }
})(typeof window !== "undefined" ? window : this);
